import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Plus, Tag } from 'lucide-react';
import { parseApiError } from '../../lib/parseApiError';
import { uid } from '../../lib/uid';
import { TIPI_PAGAMENTO, VOCE_VUOTA, DATI_DDT_VUOTI, DATI_FATTURA_VUOTI } from '../../lib/documentiConst';
import CatalogoDialog from './CatalogoDialog';
import { VoceRiga } from './VoceRiga';
import { DatiDdtCard } from './DatiDdtCard';
import { DatiFatturaCard } from './DatiFatturaCard';
import { ClienteSearchField } from './ClienteSearchField';
import { DestinazioneSection } from './DestinazioneSection';
import { TipoDocumentoSelect } from './TipoDocumentoSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nuovaVoce = () => ({ ...VOCE_VUOTA, _uid: uid() });

const formVuoto = () => ({
  cliente_id: '',
  tipo: 'preventivo',
  oggetto: '',
  note: '',
  validita_giorni: 10,
  iva_percentuale: 22,
  tipo_pagamento: '',
  pagamento_testo: '',
  data_documento: '',
  destinazione: '',
  voci: [nuovaVoce()],
  dati_ddt: { ...DATI_DDT_VUOTI },
  dati_fattura: { ...DATI_FATTURA_VUOTI },
  riferimento_preventivo_id: '',
  riferimento_ddt_ids: []
});

// Storage pagamento: stringa singola "OPZIONE_MENU - testo libero" (separatore " - ")
const parsePagamento = (raw) => {
  const v = (raw || '').trim();
  if (!v) return { sel: '', testo: '' };
  if (TIPI_PAGAMENTO.includes(v)) return { sel: v, testo: '' };
  for (const opt of TIPI_PAGAMENTO) {
    const prefix = `${opt} - `;
    if (v.startsWith(prefix)) return { sel: opt, testo: v.slice(prefix.length) };
  }
  return { sel: '', testo: v };
};

const formDaDocumento = (doc) => {
  const pagamento = parsePagamento(doc.tipo_pagamento || '');
  return {
    cliente_id: doc.cliente_id || '',
    tipo: doc.tipo || 'preventivo',
    oggetto: doc.oggetto || '',
    note: doc.note || '',
    validita_giorni: doc.validita_giorni || 10,
    iva_percentuale: doc.iva_percentuale || 22,
    tipo_pagamento: pagamento.sel,
    pagamento_testo: pagamento.testo,
    data_documento: doc.data_documento || '',
    destinazione: doc.destinazione || '',
    voci: doc.voci?.length > 0 ? doc.voci.map(v => ({
      descrizione: v.descrizione || '',
      quantita: v.quantita || 1,
      prezzo_unitario: v.prezzo_unitario || 0,
      sconto_percentuale: v.sconto_percentuale || 0,
      unita_misura: v.unita_misura || 'pz',
      codice_articolo: v.codice_articolo || '',
      evidenzia: v.evidenzia || false,
      escludi_totale: v.escludi_totale || false,
      is_titolo: v.is_titolo || false,
      _uid: uid()
    })) : [nuovaVoce()],
    dati_ddt: doc.dati_ddt || { ...DATI_DDT_VUOTI },
    dati_fattura: doc.dati_fattura || { ...DATI_FATTURA_VUOTI },
    riferimento_preventivo_id: doc.riferimento_preventivo_id || '',
    riferimento_ddt_ids: doc.riferimento_ddt_ids || []
  };
};

const buildPayload = (formData, buildPagamento) => {
  const data = {
    cliente_id: formData.cliente_id,
    tipo: formData.tipo,
    oggetto: formData.oggetto,
    note: formData.note,
    validita_giorni: formData.validita_giorni,
    iva_percentuale: formData.iva_percentuale,
    tipo_pagamento: buildPagamento(),
    data_documento: formData.data_documento || null,
    destinazione: formData.destinazione || '',
    voci: formData.voci.map(v => ({
      descrizione: v.descrizione,
      quantita: parseFloat(v.quantita) || 1,
      prezzo_unitario: parseFloat(v.prezzo_unitario) || 0,
      sconto_percentuale: parseFloat(v.sconto_percentuale) || 0,
      unita_misura: v.unita_misura || 'pz',
      codice_articolo: v.codice_articolo || null,
      evidenzia: !!v.evidenzia,
      escludi_totale: !!v.escludi_totale,
      is_titolo: !!v.is_titolo
    })),
    riferimento_preventivo_id: formData.riferimento_preventivo_id || null,
    riferimento_ddt_ids: formData.riferimento_ddt_ids || []
  };
  if (formData.tipo === 'ddt') {
    data.dati_ddt = {
      causale_trasporto: formData.dati_ddt.causale_trasporto || 'Vendita',
      aspetto_beni: formData.dati_ddt.aspetto_beni || 'A vista',
      peso_kg: formData.dati_ddt.peso_kg ? parseFloat(formData.dati_ddt.peso_kg) : null,
      numero_colli: parseInt(formData.dati_ddt.numero_colli) || 1,
      porto: formData.dati_ddt.porto || 'Franco',
      vettore: formData.dati_ddt.vettore || null,
      data_trasporto: formData.dati_ddt.data_trasporto || null,
      ora_trasporto: formData.dati_ddt.ora_trasporto || null,
      destinazione_diversa: formData.dati_ddt.destinazione_diversa || null
    };
  }
  if (formData.tipo === 'fattura' || formData.tipo === 'nota_credito') {
    data.dati_fattura = {
      data_scadenza_pagamento: formData.dati_fattura.data_scadenza_pagamento || null,
      codice_destinatario_sdi: formData.dati_fattura.codice_destinatario_sdi || null,
      pec_destinatario: formData.dati_fattura.pec_destinatario || null,
      riferimento_ddt: formData.dati_fattura.riferimento_ddt || null,
      riferimento_ordine: formData.dati_fattura.riferimento_ordine || null,
      pagato: formData.dati_fattura.pagato || false,
      metodo_pagamento_sdi: formData.dati_fattura.metodo_pagamento_sdi || null
    };
  }
  return data;
};

/** Dialog di creazione/modifica documento (preventivo, offerta, DDT, fattura, nota credito). */
export default function DocumentoFormDialog({ open, onOpenChange, editingDocumento, clienti, tipiVisibili, onSaved }) {
  const [formData, setFormData] = useState(formVuoto);
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [articoloSearch, setArticoloSearch] = useState({});
  const [articoloResults, setArticoloResults] = useState({});
  const [catalogoOpen, setCatalogoOpen] = useState(false);
  const [catalogoTarget, setCatalogoTarget] = useState(0);
  const [puntiVenditaCliente, setPuntiVenditaCliente] = useState([]);
  const [impiantiCliente, setImpiantiCliente] = useState([]);
  const savedRef = useRef(false);

  const buildPagamento = () => {
    const sel = (formData.tipo_pagamento || '').trim();
    const txt = (formData.pagamento_testo || '').trim();
    if (sel && txt) return `${sel} - ${txt}`;
    return sel || txt;
  };

  const fetchPuntiVendita = useCallback(async (clienteId) => {
    if (!clienteId) {
      setPuntiVenditaCliente([]);
      setImpiantiCliente([]);
      return;
    }
    const [pvRes, impRes] = await Promise.all([
      axios.get(`${API}/punti-vendita?cliente_id=${clienteId}`).catch(() => ({ data: [] })),
      axios.get(`${API}/impianti?cliente_id=${clienteId}`).catch(() => ({ data: [] })),
    ]);
    setPuntiVenditaCliente(pvRes.data || []);
    setImpiantiCliente(impRes.data || []);
  }, []);

  // Inizializzazione all'apertura: da documento in modifica o form vuoto
  useEffect(() => {
    if (!open) return;
    savedRef.current = false;
    setArticoloSearch({});
    setArticoloResults({});
    setClienteSearchResults([]);
    if (!editingDocumento) {
      setFormData(formVuoto());
      setClienteSearch('');
      setSelectedCliente(null);
      setPuntiVenditaCliente([]);
      setImpiantiCliente([]);
      return;
    }
    const cliente = clienti.find(c => c.id === editingDocumento.cliente_id);
    if (cliente) {
      setSelectedCliente(cliente);
      setClienteSearch(cliente.ragione_sociale);
      fetchPuntiVendita(cliente.id);
    }
    setFormData(formDaDocumento(editingDocumento));
  }, [open, editingDocumento, clienti, fetchPuntiVendita]);

  // Ricerca cliente (debounced)
  const searchClienti = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setClienteSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/clienti/search?q=${encodeURIComponent(query)}`);
      setClienteSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchClienti(clienteSearch), 300);
    return () => clearTimeout(timer);
  }, [clienteSearch, searchClienti]);

  const handleClienteSearchChange = (value) => {
    setClienteSearch(value);
    if (selectedCliente) {
      setSelectedCliente(null);
      setFormData({ ...formData, cliente_id: '' });
    }
  };

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setFormData({ ...formData, cliente_id: cliente.id });
    setClienteSearch(cliente.ragione_sociale);
    setClienteSearchResults([]);
    fetchPuntiVendita(cliente.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...buildPayload(formData, buildPagamento), bozza: false };
      if (editingDocumento) {
        await axios.put(`${API}/documenti/${editingDocumento.id}`, data);
        toast.success(editingDocumento.bozza ? 'Bozza completata: documento creato con numero definitivo' : 'Documento aggiornato');
      } else {
        await axios.post(`${API}/documenti`, data);
        toast.success('Documento creato');
      }
      savedRef.current = true;
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  // Chiusura senza salvare: conserva il lavoro come BOZZA (nuovo documento o bozza esistente)
  const hasContenuto = () =>
    (formData.oggetto || '').trim() !== '' ||
    formData.cliente_id !== '' ||
    formData.voci.some(v => (v.descrizione || '').trim() !== '' || parseFloat(v.prezzo_unitario) > 0);

  const handleDialogChange = async (o) => {
    if (!o && !savedRef.current) {
      const isNew = !editingDocumento;
      const isBozza = !!editingDocumento?.bozza;
      if ((isNew || isBozza) && hasContenuto()) {
        try {
          const data = { ...buildPayload(formData, buildPagamento), bozza: true };
          if (isBozza) {
            await axios.put(`${API}/documenti/${editingDocumento.id}`, data);
            toast.info('Bozza aggiornata — la ritrovi nella lista Documenti');
          } else {
            await axios.post(`${API}/documenti`, data);
            toast.info('Salvato come BOZZA — riprendilo dalla lista Documenti quando vuoi');
          }
          onSaved();
        } catch (err) {
          console.error('Salvataggio bozza fallito:', err);
        }
      }
    }
    onOpenChange(o);
  };

  // --- Voci ---
  const addVoce = () => setFormData({ ...formData, voci: [...formData.voci, nuovaVoce()] });

  const removeVoce = (index) => {
    if (formData.voci.length === 1) return;
    setFormData({ ...formData, voci: formData.voci.filter((_, i) => i !== index) });
    // le mappe di ricerca sono indicizzate per posizione: reset per evitare disallineamenti
    setArticoloSearch({});
    setArticoloResults({});
  };

  const updateVoce = (index, field, value) => {
    const newVoci = [...formData.voci];
    newVoci[index] = { ...newVoci[index], [field]: value };
    setFormData({ ...formData, voci: newVoci });
  };

  const moveVoce = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= formData.voci.length) return;
    const newVoci = [...formData.voci];
    [newVoci[index], newVoci[target]] = [newVoci[target], newVoci[index]];
    setFormData({ ...formData, voci: newVoci });
    // le mappe di ricerca sono indicizzate per posizione: reset per evitare disallineamenti
    setArticoloSearch({});
    setArticoloResults({});
  };

  const searchArticolo = async (index, query) => {
    setArticoloSearch({ ...articoloSearch, [index]: query });
    if (!query || query.length < 1) {
      setArticoloResults({ ...articoloResults, [index]: [] });
      return;
    }
    try {
      const response = await axios.get(`${API}/listino/search?q=${encodeURIComponent(query)}`);
      setArticoloResults({ ...articoloResults, [index]: response.data });
    } catch (error) {
      console.error('Errore ricerca articolo:', error);
    }
  };

  const selectArticolo = (index, articolo) => {
    const newVoci = [...formData.voci];
    let descrizione = articolo.descrizione;
    if (articolo.fonte === 'magazzino' && articolo.matricola) {
      descrizione = `${articolo.descrizione}\nMatricola: ${articolo.matricola}`;
    }
    newVoci[index] = {
      ...newVoci[index],
      codice_listino: articolo.codice,
      codice_articolo: articolo.codice,
      descrizione,
      prezzo_unitario: articolo.prezzo,
      unita_misura: articolo.unita_misura || 'pz'
    };
    setFormData({ ...formData, voci: newVoci });
    setArticoloSearch({ ...articoloSearch, [index]: articolo.codice });
    setArticoloResults({ ...articoloResults, [index]: [] });
  };

  const handleCodiceKeyDown = async (index, e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const q = (e.target.value || '').trim();
    if (!q) return;
    try {
      const response = await axios.get(`${API}/listino/search?q=${encodeURIComponent(q)}`);
      if (response.data.length === 1) {
        selectArticolo(index, response.data[0]);
        toast.success(`Articolo trovato: ${response.data[0].descrizione}`);
      } else if (response.data.length === 0) {
        toast.error(`Nessun articolo trovato per "${q}" (né in listino né in magazzino)`);
      } else {
        setArticoloResults({ ...articoloResults, [index]: response.data });
      }
    } catch (error) {
      console.error('Errore ricerca articolo:', error);
    }
  };

  const apriCatalogo = (index) => {
    setCatalogoTarget(index);
    setCatalogoOpen(true);
  };

  const selezionaDalCatalogo = (articolo) => {
    selectArticolo(catalogoTarget, articolo);
    setCatalogoOpen(false);
    toast.success(`Articolo ${articolo.codice} aggiunto alla riga ${catalogoTarget + 1}`);
  };

  const calcTotale = () => {
    const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
    const subtotale = formData.voci.reduce((sum, v) => {
      if (v.escludi_totale || v.is_titolo) return sum;
      const prezzo = parseFloat(v.prezzo_unitario) || 0;
      const qta = parseFloat(v.quantita) || 1;
      const sconto = r2(parseFloat(v.sconto_percentuale) || 0);
      return sum + r2(prezzo * qta * (1 - sconto / 100));
    }, 0);
    const iva = subtotale * (parseFloat(formData.iva_percentuale) / 100);
    return { subtotale, iva, totale: subtotale + iva };
  };

  const totali = calcTotale();

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="sm:max-w-[96vw] xl:max-w-[1500px] max-h-[95vh] overflow-y-auto rounded-md">
          <DialogHeader>
            <DialogTitle className="font-mono uppercase tracking-wider">
              {editingDocumento?.bozza
                ? `Completa BOZZA ${editingDocumento.tipo?.toUpperCase()}`
                : editingDocumento ? `Modifica ${editingDocumento.tipo?.toUpperCase()} #${editingDocumento.numero}` : 'Nuovo Documento'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <TipoDocumentoSelect
                tipo={formData.tipo}
                editing={!!editingDocumento}
                tipiVisibili={tipiVisibili}
                onChange={(value) => setFormData({ ...formData, tipo: value })}
              />
              <ClienteSearchField
                clienteSearch={clienteSearch}
                results={clienteSearchResults}
                selectedCliente={selectedCliente}
                onSearchChange={handleClienteSearchChange}
                onSelect={handleSelectCliente}
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase">Oggetto *</Label>
              <Input
                value={formData.oggetto}
                onChange={(e) => setFormData({...formData, oggetto: e.target.value})}
                className="rounded-none"
                required
                data-testid="documento-oggetto"
              />
            </div>

            {selectedCliente && (
              <DestinazioneSection
                selectedCliente={selectedCliente}
                puntiVendita={puntiVenditaCliente}
                impianti={impiantiCliente}
                destinazione={formData.destinazione}
                onDestinazione={(v) => setFormData({ ...formData, destinazione: v })}
                onAppendImpianto={(label) => setFormData(prev => ({
                  ...prev,
                  destinazione: prev.destinazione ? `${prev.destinazione}\nImpianto: ${label}` : `Impianto: ${label}`
                }))}
              />
            )}

            {/* Voci */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-mono uppercase">Voci</Label>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  Cerca per codice/matricola o spara il barcode (listino + magazzino)
                </p>
              </div>
              {formData.voci.map((voce, index) => (
                <VoceRiga
                  key={voce._uid}
                  voce={voce}
                  index={index}
                  searchValue={articoloSearch[index]}
                  results={articoloResults[index]}
                  onSearchChange={searchArticolo}
                  onCodiceKeyDown={handleCodiceKeyDown}
                  onSelectArticolo={selectArticolo}
                  onUpdate={updateVoce}
                  onRemove={removeVoce}
                  onMove={moveVoce}
                  count={formData.voci.length}
                  onApriCatalogo={apriCatalogo}
                  removable={formData.voci.length > 1}
                />
              ))}
              <Button type="button" variant="outline" onClick={addVoce} className="rounded-none">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Voce
              </Button>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase">IVA %</Label>
                <Input
                  type="number"
                  value={formData.iva_percentuale}
                  onChange={(e) => setFormData({...formData, iva_percentuale: parseFloat(e.target.value) || 0})}
                  className="rounded-md"
                  data-testid="documento-iva"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Validità (gg)</Label>
                <Input
                  type="number"
                  value={formData.validita_giorni}
                  onChange={(e) => setFormData({...formData, validita_giorni: parseInt(e.target.value) || 30})}
                  className="rounded-md"
                  data-testid="documento-validita"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Data documento</Label>
                <Input
                  type="date"
                  value={formData.data_documento || ''}
                  onChange={(e) => setFormData({...formData, data_documento: e.target.value})}
                  className="rounded-md"
                  data-testid="documento-data"
                />
                <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                  Vuota = data di oggi. Puoi impostare anche una data passata.
                </p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Tipo Pagamento</Label>
                <Select
                  value={formData.tipo_pagamento || "none"}
                  onValueChange={(value) => setFormData({...formData, tipo_pagamento: value === "none" ? "" : value})}
                >
                  <SelectTrigger className="rounded-md" data-testid="documento-pagamento">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-md">
                    <SelectItem value="none">Non specificato</SelectItem>
                    {TIPI_PAGAMENTO.map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={formData.pagamento_testo || ''}
                  onChange={(e) => setFormData({...formData, pagamento_testo: e.target.value})}
                  placeholder="Accordo personalizzato (opzionale)"
                  className="rounded-md mt-2 text-sm"
                  data-testid="documento-pagamento-testo"
                />
                <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                  Lascia la tendina su un&apos;opzione, oppure scrivi qui il tuo accordo. Puoi anche combinare entrambi.
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-mono uppercase text-muted-foreground">Totale</p>
                <p className="text-2xl font-black">{totali.totale.toFixed(2)} EUR</p>
                <p className="text-xs text-muted-foreground">IVA: {totali.iva.toFixed(2)} EUR</p>
              </div>
            </div>

            {formData.tipo === 'ddt' && (
              <DatiDdtCard
                dati={formData.dati_ddt}
                onChange={(patch) => setFormData(prev => ({ ...prev, dati_ddt: { ...prev.dati_ddt, ...patch } }))}
              />
            )}

            {(formData.tipo === 'fattura' || formData.tipo === 'nota_credito') && (
              <DatiFatturaCard
                dati={formData.dati_fattura}
                onChange={(patch) => setFormData(prev => ({ ...prev, dati_fattura: { ...prev.dati_fattura, ...patch } }))}
              />
            )}

            <div>
              <Label className="text-xs font-mono uppercase">Note</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                className="rounded-none"
                data-testid="documento-note"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">
                Annulla
              </Button>
              <Button type="submit" className="rounded-none font-mono uppercase" disabled={!selectedCliente} data-testid="save-documento-btn">
                {editingDocumento ? 'Aggiorna Documento' : 'Crea Documento'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <CatalogoDialog
        open={catalogoOpen}
        onOpenChange={setCatalogoOpen}
        targetIndex={catalogoTarget}
        onSelect={selezionaDalCatalogo}
      />
    </>
  );
}
